import React, { useEffect, useState } from 'react';
import LudoBoard from './LudoBoard';
import DiceComponent from './DiceComponent';
import WaitingRoom from '../WaitingRoom';
import { useLudoGame } from '../../hooks/useLudoGame';
import { useLegacyGameState } from '../../hooks/useLegacyGameState';
import { useNaijaLudoState } from '../../hooks/useNaijaLudoState';
import { useGameInteraction } from '../../hooks/useGameInteraction';
import { BoardGameState } from '../../hooks/useBoardState';
import './LudoGame.css';

interface SecureNaijaLudoGameProps {
  gameId?: string | null;
}

const SecureNaijaLudoGame: React.FC<SecureNaijaLudoGameProps> = ({
  gameId: directGameId,
}) => {
  // Core game logic
  const {
    gameId,
    backendGameState,
    availableMoves,
    isLoading,
    error,
    isRolling,
    celebratingPiece,
    lastDiceResult,
    startGame,
    rollDice,
    movePiece,
    refreshGameState,
    setError,
  } = useLudoGame({ gameType: 'naija-ludo', gameId: directGameId });

  // Legacy state conversion for board compatibility
  const { legacyGameState } = useLegacyGameState({
    backendGameState,
    availableMoves,
  });

  // Convert LegacyGameState to BoardGameState for board component
  const boardGameState: BoardGameState = {
    players:
      legacyGameState.players.length > 0
        ? legacyGameState.players.map((player) => ({
            id: player.id,
            name: player.name,
            color: player.color,
            pieces: player.pieces.map((piece) => ({
              id: piece.id,
              position: piece.position,
              isInHomeStretch: piece.isInHomeStretch,
              hasCompletedCircuit: piece.hasCompletedCircuit,
              lastMoveId: piece.lastMoveId,
            })),
          }))
        : [],
    currentPlayer: legacyGameState.currentPlayer,
    diceValue: legacyGameState.diceValue,
    gameStatus: legacyGameState.gameStatus,
    winner: legacyGameState.winner,
  };

  // Naija Ludo specific state
  const {
    diceRoll,
    moveOptions,
    selectedMoveOption,
    selectedDiceValue,
    diceValueMoves,
    setDiceRoll,
    setDiceValueMoves,
    handleMoveOptionSelect,
    handleDiceValueSelect,
    handleExecuteMove,
    resetSelections,
  } = useNaijaLudoState();

  // Add missing piece highlighting state from original implementation
  const [highlightedPieces, setHighlightedPieces] = useState<number[]>([]);

  // Custom dice value selection handler (restored from original implementation)
  const handleCustomDiceValueSelect = (diceValue: number) => {
    console.log(`✅ Dice value selected: ${diceValue}`);
    console.log('📋 Available moves for all dice values:', diceValueMoves);
    console.log(
      `🎯 Pieces for selected dice value ${diceValue}:`,
      diceValueMoves[diceValue] || []
    );

    // Set the selected dice value using the hook
    handleDiceValueSelect(diceValue);

    // Highlight pieces that can move with this dice value
    const piecesToHighlight = diceValueMoves[diceValue] || [];
    setHighlightedPieces(piecesToHighlight);

    console.log(
      `🌟 HIGHLIGHTING ${piecesToHighlight.length} PIECES:`,
      piecesToHighlight
    );
  };

  // Custom piece click handler for Naija Ludo (restored from original implementation)
  const handleNaijaPieceClick = async (pieceIndex: number) => {
    console.log(
      `Piece clicked: ${pieceIndex}, Selected dice: ${selectedDiceValue}, Highlighted pieces:`,
      highlightedPieces
    );

    // Only allow piece movement if dice value is selected and piece is highlighted
    if (!selectedDiceValue || !highlightedPieces.includes(pieceIndex)) {
      console.log(
        `❌ Piece ${pieceIndex} cannot be moved - not highlighted or no dice selected`
      );
      return;
    }

    console.log(
      `✅ Moving piece ${pieceIndex} with dice value ${selectedDiceValue}`
    );

    if (backendGameState) {
      const currentPlayerIndex = backendGameState.currentPlayerIndex;
      await movePiece(currentPlayerIndex, pieceIndex, selectedDiceValue);

      // Reset selections after move
      resetSelections();
      setHighlightedPieces([]);
    }
  };

  // Update dice roll state when backend state changes
  useEffect(() => {
    // Use lastDiceResult from API response instead of backendGameState.lastDiceRoll
    if (lastDiceResult && typeof lastDiceResult === 'object') {
      // For Naija Ludo, check if we have dice1 and dice2 (full Naija dice structure)
      if (lastDiceResult.dice1 && lastDiceResult.dice2) {
        const { dice1, dice2 } = lastDiceResult;

        setDiceRoll({
          values: [dice1, dice2],
          canRollAgain: false, // Will be determined by game rules
          isEndTurn: false,
        });

        // Calculate available moves for each dice value (restored original logic)
        const movesByDice: { [key: number]: number[] } = {
          [dice1]: [],
          [dice2]: [],
          [dice1 + dice2]: [], // Combined move
        };

        console.log(
          `🎲 Processing dice roll: ${dice1} + ${dice2} = ${dice1 + dice2}`
        );
        console.log('🎯 Available moves from backend:', availableMoves);

        // Process each available move from backend
        if (availableMoves && Array.isArray(availableMoves)) {
          availableMoves.forEach((move: any) => {
            console.log('📝 Processing move:', move);

            if (move.diceValue) {
              const diceValue = move.diceValue;
              const pieceIndex = move.pieceIndex || 0;

              // Individual dice moves
              if (diceValue === dice1) {
                movesByDice[dice1].push(pieceIndex);
                console.log(`✅ Added piece ${pieceIndex} to dice1 (${dice1})`);
              }

              if (diceValue === dice2) {
                movesByDice[dice2].push(pieceIndex);
                console.log(`✅ Added piece ${pieceIndex} to dice2 (${dice2})`);
              }

              // Combined dice moves
              if (diceValue === dice1 + dice2) {
                movesByDice[dice1 + dice2].push(pieceIndex);
                console.log(
                  `✅ Added piece ${pieceIndex} to combined (${dice1 + dice2})`
                );
              }
            }
          });
        }

        console.log('🎯 Final dice value moves:', movesByDice);
        setDiceValueMoves(movesByDice);
      }
    } else if (lastDiceResult && typeof lastDiceResult.value === 'number') {
      // For classic ludo, just show single dice value
      setDiceRoll({
        values: [lastDiceResult.value],
        canRollAgain: false,
        isEndTurn: false,
      });
    }
  }, [lastDiceResult, availableMoves, setDiceRoll, setDiceValueMoves]);

  // Game interaction handlers
  const { handleSquareClick } = useGameInteraction({
    backendGameState,
    legacyGameState,
    availableMoves,
    movePiece,
    setError,
  });

  // Handle Naija Ludo specific move execution
  const handleNaijaMove = async () => {
    const moveData = handleExecuteMove();
    if (moveData && backendGameState) {
      const currentPlayerIndex = backendGameState.currentPlayerIndex;
      const pieceIndex = moveData.moveOption.pieceIndex;
      await movePiece(currentPlayerIndex, pieceIndex, moveData.diceValue);
      resetSelections();
    }
  };

  // Loading state
  if (isLoading && !backendGameState) {
    return (
      <div className="secure-naija-ludo-game">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Initializing Naija Ludo game...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="secure-naija-ludo-game">
        <div className="error-container">
          <h3>Game Error</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Reload Game</button>
        </div>
      </div>
    );
  }

  // Waiting room (game not started)
  if (backendGameState?.gamePhase === 'waiting') {
    return (
      <div className="secure-naija-ludo-game">
        <WaitingRoom
          gameType="naija-ludo"
          gameId={gameId || ''}
          playersConnected={backendGameState.players.length}
          maxPlayers={4}
          isLoading={isLoading}
          onStartGame={startGame}
          playerNames={backendGameState.players.map((p: any) => p.displayName)}
        />
      </div>
    );
  }

  // Main game interface
  return (
    <div className="secure-naija-ludo-game">
      {/* Game Header */}
      <div className="game-header">
        <h2>Secure Naija Ludo Game</h2>
        {gameId && (
          <div className="game-info">
            <span>Game ID: {gameId}</span>
            <button onClick={refreshGameState} className="refresh-btn">
              🔄 Refresh
            </button>
          </div>
        )}
      </div>

      {/* Current Player Info */}
      {backendGameState && (
        <div className="current-player-info">
          <h3>
            Current Player:{' '}
            <span
              style={{
                color:
                  backendGameState.players[backendGameState.currentPlayerIndex]
                    ?.color,
              }}
            >
              {
                backendGameState.players[backendGameState.currentPlayerIndex]
                  ?.displayName
              }
            </span>{' '}
            (
            {
              backendGameState.players[backendGameState.currentPlayerIndex]
                ?.color
            }
            )
          </h3>

          {/* Show all players and their colors */}
          <div className="players-info">
            <h4>Players:</h4>
            {backendGameState.players.map((player: any, index: number) => (
              <div
                key={player.id}
                className={`player-info ${index === backendGameState.currentPlayerIndex ? 'current' : ''}`}
              >
                <span style={{ color: player.color, fontWeight: 'bold' }}>
                  ●
                </span>
                <span>{player.displayName}</span>
                <span className="color-name">({player.color})</span>
                {index === backendGameState.currentPlayerIndex && (
                  <span className="current-indicator">← Your turn</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Naija Ludo Dice Component */}
      <div className="dice-section">
        <DiceComponent
          onRoll={rollDice}
          isRolling={isRolling}
          canRoll={!isRolling}
          gameVariant="naija"
          diceRoll={diceRoll}
          moveOptions={moveOptions}
          selectedMoveOption={selectedMoveOption}
          selectedDiceValue={selectedDiceValue}
          availableMoves={diceValueMoves}
          onMoveOptionSelect={handleMoveOptionSelect}
          onDiceValueSelect={handleCustomDiceValueSelect}
          onExecuteMove={handleNaijaMove}
        />
      </div>

      {/* Game Board */}
      <div className="game-board-container">
        {boardGameState.players.length > 0 ? (
          <LudoBoard
            gameState={boardGameState}
            onPieceClick={handleNaijaPieceClick}
            onSquareClick={handleSquareClick}
            validMoves={highlightedPieces.length > 0 ? highlightedPieces : []}
            celebratingPiece={celebratingPiece}
          />
        ) : (
          <div className="loading-board">
            <p>Loading game board...</p>
          </div>
        )}
      </div>

      {/* Game Status */}
      {legacyGameState.gameStatus === 'finished' && legacyGameState.winner && (
        <div className="game-finished">
          <h2>🎉 Game Finished! 🎉</h2>
          <p>Winner: {legacyGameState.winner}</p>
          <button onClick={() => window.location.reload()}>Play Again</button>
        </div>
      )}

      {/* Debug Info (Development Only) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="debug-info">
          <h4>Debug Info</h4>
          <details>
            <summary>Backend Game State</summary>
            <pre>{JSON.stringify(backendGameState, null, 2)}</pre>
          </details>
          <details>
            <summary>Available Moves</summary>
            <pre>{JSON.stringify(availableMoves, null, 2)}</pre>
          </details>
          <details>
            <summary>Naija Ludo State</summary>
            <pre>
              {JSON.stringify(
                {
                  diceRoll,
                  moveOptions,
                  selectedMoveOption,
                  selectedDiceValue,
                  diceValueMoves,
                },
                null,
                2
              )}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
};

export default SecureNaijaLudoGame;
